import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Lamp,
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  TreeDeciduous,
  Home,
  Gift,
  Flower2,
  Award,
  Ruler,
  Palette
} from 'lucide-react';
import { toast } from 'sonner';

// Wood types
const WOOD_TYPES = [
  { value: 'ulivo', label: 'Legno di ulivo', icon: TreeDeciduous },
  { value: 'noce', label: 'Legno di noce', icon: TreeDeciduous },
  { value: 'ciliegio', label: 'Legno di ciliegio', icon: TreeDeciduous },
  { value: 'rovere', label: 'Legno di rovere', icon: TreeDeciduous },
  { value: 'castagno', label: 'Legno di castagno', icon: TreeDeciduous },
  { value: 'frassino', label: 'Legno di frassino', icon: TreeDeciduous },
  { value: 'abete', label: 'Abete antico / Recupero', icon: TreeDeciduous },
  { value: 'radica', label: 'Radica', icon: Sparkles },
  { value: 'misto', label: 'Legni misti / Intarsio', icon: Sparkles },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Object types
const OBJECT_TYPES = [
  { value: 'vaso', label: 'Vaso / Portafiori', icon: Flower2 },
  { value: 'ciotola', label: 'Ciotola / Centrotavola', icon: Home },
  { value: 'candelabro', label: 'Candelabro / Portacandele', icon: Lamp },
  { value: 'lampada', label: 'Base lampada', icon: Lamp },
  { value: 'orologio', label: 'Orologio da parete', icon: Clock },
  { value: 'mensola', label: 'Mensola decorativa', icon: Home },
  { value: 'appendiabiti', label: 'Appendiabiti / Attaccapanni', icon: Home },
  { value: 'portaombrelli', label: 'Portaombrelli', icon: Home },
  { value: 'portagioie', label: 'Portagioie', icon: Sparkles },
  { value: 'altro', label: 'Altro oggetto decorativo', icon: Star },
];

// Style options
const STYLE_OPTIONS = [
  { value: 'moderno', label: 'Moderno / Minimal', icon: Star },
  { value: 'rustico', label: 'Rustico / Country', icon: TreeDeciduous },
  { value: 'classico', label: 'Classico / Elegante', icon: Award },
  { value: 'scandinavo', label: 'Scandinavo / Nordico', icon: Home },
  { value: 'industriale', label: 'Industriale / Urban', icon: Lamp },
  { value: 'shabby', label: 'Shabby chic', icon: Heart },
  { value: 'etnico', label: 'Etnico / Bohemian', icon: Flower2 },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Finish options
const FINISH_OPTIONS = [
  { value: 'naturale', label: 'Finitura naturale (olio)', icon: TreeDeciduous },
  { value: 'cerato', label: 'Cerato (cera d\'api)', icon: Star },
  { value: 'verniciato', label: 'Verniciato / Laccato', icon: Palette },
  { value: 'sbiancato', label: 'Sbiancato / Decapato', icon: Sparkles },
  { value: 'bruciato', label: 'Bruciato (Shou Sugi Ban)', icon: TreeDeciduous },
  { value: 'colorato', label: 'Colorato', icon: Palette },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Purpose/Occasion
const PURPOSE_OPTIONS = [
  { value: 'arredo', label: 'Arredo casa', icon: Home },
  { value: 'regalo', label: 'Come regalo', icon: Gift },
  { value: 'matrimonio', label: 'Regalo matrimonio', icon: Heart },
  { value: 'inaugurazione', label: 'Inaugurazione casa', icon: Home },
  { value: 'ufficio', label: 'Per ufficio / Studio', icon: Award },
  { value: 'ristorante', label: 'Per attività commerciale', icon: Star },
  { value: 'collezione', label: 'Per collezione', icon: Sparkles },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '30-60', label: '€30 - €60', description: 'Oggetto semplice' },
  { value: '60-120', label: '€60 - €120', description: 'Legno pregiato' },
  { value: '120-200', label: '€120 - €200', description: 'Lavorazione dettagliata' },
  { value: '200-350', label: '€200 - €350', description: 'Pezzo importante' },
  { value: '350-600', label: '€350 - €600', description: 'Opera d\'arredo' },
  { value: '600+', label: '€600+', description: 'Pezzo unico / Grande' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '1week', label: '1 Settimana', description: 'Urgente' },
  { value: '2weeks', label: '2 Settimane', description: 'Prioritario' },
  { value: '3weeks', label: '3 Settimane', description: 'Standard' },
  { value: '1month', label: '1 Mese', description: 'Con calma' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example objects
const OBJECT_EXAMPLES = [
  {
    idea: "Vaso tornito per orchidee, stile minimal",
    result: "Vaso in noce tornito a mano, interno impermeabilizzato, forma conica 25cm, finitura naturale opaca",
    image: "https://images.unsplash.com/photo-1485955900006-10f4d324d411?w=400&h=300&fit=crop",
    category: "Vaso"
  },
  {
    idea: "Orologio da parete per soggiorno moderno",
    result: "Orologio Ø50cm in rovere massello, numeri incisi, meccanismo silenzioso, design minimalista",
    image: "https://images.unsplash.com/photo-1563861826100-9cb868fdbe1c?w=400&h=300&fit=crop",
    category: "Orologio"
  },
  {
    idea: "Centrotavola per tavolo ristorante rustico",
    result: "Ciotola ovale 60cm in ulivo con venature naturali, bordo live edge, trattamento food-safe",
    image: "https://images.unsplash.com/photo-1604578762246-41134e37f9cc?w=400&h=300&fit=crop",
    category: "Centrotavola"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho ordinato un vaso tornito in ulivo come centrotavola. Le venature del legno sono incredibili e ogni ospite lo nota. Un pezzo che rende unica la mia sala da pranzo.",
    author: "Serena P.",
    location: "Napoli"
  },
  {
    text: "Cercavo un orologio da parete che non fosse il solito oggetto anonimo. Quello in rovere che mi hanno fatto è perfetto per il mio loft, minimalista ma caldo.",
    author: "Andrea M.",
    location: "Milano"
  },
  {
    text: "Per il mio B&B volevo oggetti che raccontassero la Toscana. Le ciotole in castagno che ho commissionato sono diventate parte dell'identità del locale.",
    author: "Lucia F.",
    location: "Arezzo"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Che oggetti decorativi si possono realizzare in legno?",
    answer: "Praticamente tutto ciò che immagini: vasi, ciotole, candelabri, orologi, lampade, portaoggetti, appendiabiti, mensole scultoree. Se hai un'idea, il falegname può realizzarla."
  },
  {
    question: "Il legno va bene per oggetti a contatto con l'acqua?",
    answer: "Sì, con i giusti trattamenti. Per vasi e ciotole si usano impermeabilizzanti atossici o inserti in vetro. L'ulivo è naturalmente resistente all'umidità."
  },
  {
    question: "Posso avere un oggetto da una foto che ho visto?",
    answer: "Certamente! Condividi l'immagine di riferimento e l'artigiano creerà qualcosa di simile, adattato ai tuoi gusti e alle possibilità del legno scelto."
  },
  {
    question: "Quanto costa un oggetto decorativo artigianale?",
    answer: "Dipende dalla complessità: piccoli oggetti semplici partono da €30-50, pezzi medi lavorati €100-200, oggetti grandi o con lavorazioni particolari €300+."
  },
  {
    question: "È possibile abbinare più oggetti coordinati?",
    answer: "Assolutamente! Molti clienti ordinano set coordinati (es: ciotole di diverse misure, o orologio + mensole). L'artigiano può usare lo stesso legno e stile per creare armonia."
  }
];

export default function OggettiDecorativiRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    wood_type: '',
    object_type: '',
    style: '',
    finish: '',
    purpose: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Oggetti Decorativi Su Misura | Falegnameria Artigianale | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `decor-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi l\'oggetto che desideri');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_decor_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const woodLabel = WOOD_TYPES.find(t => t.value === formData.wood_type)?.label || '';
      const objectLabel = OBJECT_TYPES.find(t => t.value === formData.object_type)?.label || '';
      const styleLabel = STYLE_OPTIONS.find(t => t.value === formData.style)?.label || '';
      const finishLabel = FINISH_OPTIONS.find(t => t.value === formData.finish)?.label || '';
      const purposeLabel = PURPOSE_OPTIONS.find(p => p.value === formData.purpose)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      let fullDescription = formData.description;
      
      const details = [];
      if (objectLabel) details.push(`🏺  Tipo: ${objectLabel}`);
      if (woodLabel) details.push(`🌳  Legno: ${woodLabel}`);
      if (styleLabel) details.push(`🎨  Stile: ${styleLabel}`);
      if (finishLabel) details.push(`✨  Finitura: ${finishLabel}`);
      if (purposeLabel) details.push(`🎯  Utilizzo: ${purposeLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${objectLabel} in ${woodLabel}`.trim() || 'Oggetto decorativo su misura',
        description: fullDescription,
        category: 'Oggetti Decorativi',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dai falegnami.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('decor-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Oggetti Decorativi Su Misura | Falegnameria Artigianale Italiana | Rarifex</title>
        <meta name="description" content="Oggetti decorativi in legno su misura: vasi, ciotole, orologi, candelabri realizzati da maestri falegnami italiani. Artigianato Made in Italy. Gratuito e senza impegno." />
        <meta name="keywords" content="oggetti decorativi legno, vaso legno, ciotola legno, orologio legno, centrotavola legno, arredo legno artigianale, falegnameria" />
        <link rel="canonical" href="https://www.rarifex.com/oggetti-decorativi-su-misura" />
        <meta property="og:title" content="Oggetti Decorativi Su Misura | Falegnameria Artigianale Italiana | Rarifex" />
        <meta property="og:description" content="Oggetti decorativi in legno su misura realizzati da maestri falegnami italiani. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/oggetti-decorativi-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-emerald-50/50 via-white to-amber-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Lamp className="w-4 h-4" />
                    Arredo Artigianale
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Oggetti unici{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-amber-600">
                      per la tua casa
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Maestri falegnami creano l'oggetto perfetto per i tuoi spazi.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Vasi, ciotole, orologi, candelabri: ogni pezzo racconta una storia.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Artigiani verificati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-slate-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="decor-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo del tuo progetto
                        </Label>
                        <Input
                          placeholder="Es: Vaso per orchidee stile minimal"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="decor-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi l'oggetto che desideri *
                        </Label>
                        <Textarea
                          placeholder="Racconta la tua idea... Tipo di oggetto, stile, dimensioni, dove lo userai. Allega foto di riferimento se ne hai."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="decor-description-textarea"
                        />
                      </div>
                      
                      {/* Object Type & Wood Type */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tipo</Label>
                          <select
                            value={formData.object_type}
                            onChange={(e) => handleInputChange('object_type', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="decor-type-select"
                          >
                            <option value="">Seleziona tipo...</option>
                            {OBJECT_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Legno</Label>
                          <select
                            value={formData.wood_type}
                            onChange={(e) => handleInputChange('wood_type', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="decor-wood-select"
                          >
                            <option value="">Seleziona legno...</option>
                            {WOOD_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Style & Finish */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Stile</Label>
                          <select
                            value={formData.style}
                            onChange={(e) => handleInputChange('style', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="decor-style-select"
                          >
                            <option value="">Seleziona...</option>
                            {STYLE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Finitura</Label>
                          <select
                            value={formData.finish}
                            onChange={(e) => handleInputChange('finish', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="decor-finish-select"
                          >
                            <option value="">Seleziona...</option>
                            {FINISH_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Purpose & Budget */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Utilizzo</Label>
                          <select
                            value={formData.purpose}
                            onChange={(e) => handleInputChange('purpose', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="decor-purpose-select"
                          >
                            <option value="">Seleziona...</option>
                            {PURPOSE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="decor-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Timeline */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Tempi</Label>
                        <select
                          value={formData.timeline}
                          onChange={(e) => handleInputChange('timeline', e.target.value)}
                          className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                          data-testid="decor-timeline-select"
                        >
                          <option value="">Seleziona...</option>
                          {TIMELINE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* File Upload */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Immagini di riferimento (opzionale)
                        </Label>
                        <div className="mt-1.5">
                          <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors">
                            <Upload className="w-5 h-5 text-slate-400" />
                            <span className="text-sm text-slate-500">
                              {uploadingFile ? 'Caricamento...' : 'Carica immagini (max 5)'}
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handleFileUpload}
                              className="hidden"
                              disabled={uploadingFile}
                            />
                          </label>
                        </div>
                        
                        {formData.attachments.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {formData.attachments.map((url, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={url}
                                  alt={`Riferimento ${index + 1}`}
                                  className="w-16 h-16 object-cover rounded-lg"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeAttachment(index)}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-emerald-600 to-amber-600 hover:from-emerald-700 hover:to-amber-700 text-white py-6 text-lg font-semibold shadow-lg"
                        data-testid="decor-submit-button"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Invio in corso...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5 mr-2" />
                            Richiedi Preventivi Gratuiti
                          </>
                        )}
                      </Button>
                      
                      <p className="text-xs text-center text-slate-500">
                        Riceverai proposte da falegnami verificati entro 24-48 ore
                      </p>
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Come Funziona
              </h2>
              <p className="text-lg text-slate-600">
                In 4 semplici passi avrai il tuo oggetto artigianale
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {[
                { icon: MessageSquare, title: 'Descrivi', desc: 'Raccontaci l\'oggetto che desideri' },
                { icon: Users, title: 'Ricevi', desc: 'Proposte da falegnami esperti' },
                { icon: Star, title: 'Scegli', desc: 'L\'artigiano e il progetto ideale' },
                { icon: Heart, title: 'Ammira', desc: 'Il tuo oggetto unico prende vita' },
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-100 to-amber-100 flex items-center justify-center">
                    <step.icon className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section className="py-16 bg-gradient-to-b from-emerald-50/30 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Oggetti Realizzati
              </h2>
              <p className="text-lg text-slate-600">
                Dalla richiesta all'oggetto finito
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {OBJECT_EXAMPLES.map((example, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <img
                    src={example.image}
                    alt={example.result}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-5">
                    <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-medium rounded-full mb-3">
                      {example.category}
                    </span>
                    <p className="text-sm text-slate-600 mb-2">
                      <strong>Richiesta:</strong> {example.idea}
                    </p>
                    <p className="text-sm text-slate-800">
                      <strong>Risultato:</strong> {example.result}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cosa Dicono i Clienti
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {TESTIMONIALS.map((testimonial, index) => (
                <Card key={index} className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-4 italic">"{testimonial.text}"</p>
                  <div className="text-sm">
                    <p className="font-semibold text-slate-900">{testimonial.author}</p>
                    <p className="text-slate-500">{testimonial.location}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-gradient-to-b from-white to-emerald-50/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center">
                Domande Frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, index) => (
                  <Card 
                    key={index}
                    className="overflow-hidden cursor-pointer"
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <div className="p-5 flex justify-between items-center">
                      <h3 className="font-semibold text-slate-900">{faq.question}</h3>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {expandedFaq === index && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-16 bg-gradient-to-r from-emerald-600 to-amber-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Pronto a creare il tuo oggetto unico?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Richiedi preventivi gratuiti dai migliori falegnami italiani
            </p>
            <Button
              onClick={scrollToForm}
              size="lg"
              className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold shadow-xl px-8 py-6 text-lg"
              data-testid="decor-final-cta"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Inizia Ora - È Gratis
            </Button>
          </div>
        </section>
        
        {/* Auth Prompt Modal */}
        {showAuthPrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Accedi per continuare
              </h3>
              <p className="text-slate-600 mb-6">
                Per inviare la tua richiesta e ricevere proposte dai falegnami, accedi o crea un account.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAuthPrompt(false)}
                >
                  Annulla
                </Button>
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => navigate('/login?redirect=/oggetti-decorativi-su-misura')}
                >
                  Accedi
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
